/**
 * Carrier + tracking-network registry for the /apps/shop marketing page.
 *
 * Source of truth: docs/09-Web-Projects/shop.askfinz.ai/11-SHIPPING-FULFILLMENT-
 * PROVIDERS.md (carrier coverage by region) + 12-ORDER-LIFECYCLE-AND-FULFILLMENT.
 * We surface the *carriers* a seller can ship with — never the aggregator behind
 * them (marketing copy stays non-technical and brand-neutral on plumbing).
 *
 * `domain` drives both the outbound link and the favicon (rendered via a plain
 * <img> so it needs no next/image remotePatterns entry). `services` is what the
 * platform supports for that carrier: live rates, buying a label, tracking,
 * return labels, and cross-border customs/duties where the carrier handles it.
 */

export type CarrierService =
  | "Live rates"
  | "Labels"
  | "Tracking"
  | "Returns"
  | "Cross-border";

export type Carrier = {
  name: string;
  /** Bare domain — used for the favicon and the outbound link. */
  domain: string;
  services: CarrierService[];
};

export type CarrierRegion = {
  region: string;
  blurb: string;
  carriers: Carrier[];
};

const FULL: CarrierService[] = ["Live rates", "Labels", "Tracking", "Returns"];
const GLOBAL: CarrierService[] = ["Live rates", "Labels", "Tracking", "Returns", "Cross-border"];
const BASIC: CarrierService[] = ["Labels", "Tracking"];

export const CARRIER_REGIONS: CarrierRegion[] = [
  {
    region: "North America",
    blurb: "USPS, UPS & FedEx at pooled discount rates",
    carriers: [
      { name: "USPS", domain: "usps.com", services: FULL },
      { name: "UPS", domain: "ups.com", services: GLOBAL },
      { name: "FedEx", domain: "fedex.com", services: GLOBAL },
      { name: "Canada Post", domain: "canadapost-postescanada.ca", services: FULL },
      { name: "Purolator", domain: "purolator.com", services: BASIC },
    ],
  },
  {
    region: "United Kingdom",
    blurb: "Royal Mail, Evri & DPD",
    carriers: [
      { name: "Royal Mail", domain: "royalmail.com", services: FULL },
      { name: "Evri", domain: "evri.com", services: FULL },
      { name: "DPD UK", domain: "dpd.co.uk", services: FULL },
      { name: "Parcelforce", domain: "parcelforce.com", services: BASIC },
      { name: "Yodel", domain: "yodel.co.uk", services: BASIC },
    ],
  },
  {
    region: "Europe",
    blurb: "PostNL, DHL, DPD, GLS & the national posts",
    carriers: [
      { name: "PostNL", domain: "postnl.nl", services: FULL },
      { name: "DHL", domain: "dhl.com", services: GLOBAL },
      { name: "DPD", domain: "dpd.com", services: FULL },
      { name: "GLS", domain: "gls-group.com", services: FULL },
      { name: "bpost", domain: "bpost.be", services: BASIC },
      { name: "Colissimo", domain: "colissimo.fr", services: FULL },
      { name: "Mondial Relay", domain: "mondialrelay.fr", services: BASIC },
      { name: "Correos", domain: "correos.es", services: BASIC },
      { name: "Poste Italiane", domain: "poste.it", services: BASIC },
    ],
  },
  {
    region: "Asia-Pacific",
    blurb: "Australia Post, Japan Post & regional couriers",
    carriers: [
      { name: "Australia Post", domain: "auspost.com.au", services: FULL },
      { name: "Sendle", domain: "sendle.com", services: FULL },
      { name: "NZ Post", domain: "nzpost.co.nz", services: BASIC },
      { name: "Japan Post", domain: "japanpost.jp", services: BASIC },
      { name: "SF Express", domain: "sf-express.com", services: BASIC },
      { name: "J&T Express", domain: "jtexpress.com", services: BASIC },
      { name: "Ninja Van", domain: "ninjavan.co", services: BASIC },
    ],
  },
  {
    region: "India",
    blurb: "Delhivery, Blue Dart & India Post",
    carriers: [
      { name: "Delhivery", domain: "delhivery.com", services: FULL },
      { name: "Blue Dart", domain: "bluedart.com", services: BASIC },
      { name: "DTDC", domain: "dtdc.in", services: BASIC },
      { name: "India Post", domain: "indiapost.gov.in", services: BASIC },
      { name: "XpressBees", domain: "xpressbees.com", services: BASIC },
    ],
  },
  {
    region: "Latin America",
    blurb: "Correios & regional networks",
    carriers: [
      { name: "Correios", domain: "correios.com.br", services: BASIC },
      { name: "Mercado Envíos", domain: "mercadolibre.com", services: BASIC },
      { name: "Jadlog", domain: "jadlog.com.br", services: BASIC },
      { name: "Loggi", domain: "loggi.com", services: BASIC },
    ],
  },
  {
    region: "Middle East & Africa",
    blurb: "Aramex & DHL Express",
    carriers: [
      { name: "Aramex", domain: "aramex.com", services: GLOBAL },
      { name: "DHL Express", domain: "dhl.com", services: GLOBAL },
    ],
  },
];

/**
 * Postal + regional networks we follow end to end even when the seller didn't
 * buy the label through us — so order status and delivery updates stay live.
 */
export const TRACKING_PROVIDERS: { name: string; domain: string }[] = [
  { name: "DHL Global Mail", domain: "dhl.com" },
  { name: "China Post", domain: "chinapost.com.cn" },
  { name: "China EMS", domain: "ems.com.cn" },
  { name: "PostNord", domain: "postnord.com" },
  { name: "An Post", domain: "anpost.com" },
  { name: "Swiss Post", domain: "post.ch" },
  { name: "Singapore Post", domain: "singpost.com" },
  { name: "Hong Kong Post", domain: "hongkongpost.hk" },
  { name: "Korea Post", domain: "epost.go.kr" },
  { name: "La Poste", domain: "laposte.fr" },
  { name: "PostNL", domain: "postnl.nl" },
  { name: "OnTrac", domain: "ontrac.com" },
  { name: "Veho", domain: "shipveho.com" },
  { name: "GLS", domain: "gls-group.com" },
];

/** Total distinct carriers across all regions — for the section eyebrow. */
export const CARRIER_COUNT = CARRIER_REGIONS.reduce((n, r) => n + r.carriers.length, 0);
